import React, { useEffect, useState } from "react";

/* ─── Access tier config ─────────────────────────────────────── */
const ACCESS = {
  free: {
    label: "Free",
    bg: "rgba(16,185,129,0.10)",
    border: "rgba(16,185,129,0.25)",
    color: "#34d399",
  },
  growth: {
    label: "GROWTH",
    bg: "rgba(245,158,11,0.10)",
    border: "rgba(245,158,11,0.25)",
    color: "#fbbf24",
  },
  insight: {
    label: "INSIGHT",
    bg: "rgba(251,191,36,0.07)",
    border: "rgba(251,191,36,0.28)",
    color: "#fbbf24",
  },
} as const;

/* ─── 13 report chapters with plan access ───────────────────── */
const SECTIONS: {
  icon: string;
  title: string;
  access: keyof typeof ACCESS;
  highlight?: boolean;
  pdfOnly?: boolean;
}[] = [
  { icon: "🗺️", title: "Birth Chart",            access: "free"                        },
  { icon: "🔄", title: "Karmic Overview",         access: "free",    highlight: true    },
  { icon: "📋", title: "Report Snapshot",         access: "growth",  pdfOnly: true      },
  { icon: "🤖", title: "AI Summary",              access: "growth",  pdfOnly: true      },
  { icon: "📖", title: "How to Use",              access: "growth",  pdfOnly: true      },
  { icon: "🧠", title: "Personality Traits",      access: "growth"                      },
  { icon: "🏠", title: "Planet in Houses",        access: "growth"                      },
  { icon: "🌿", title: "Karmic Remedies",         access: "growth"                      },
  { icon: "🔔", title: "Awareness",               access: "growth"                      },
  { icon: "⚖️", title: "Strong & Weak Planets",   access: "insight"                     },
  { icon: "🔗", title: "Planet Conjunctions",     access: "insight"                     },
  { icon: "✨", title: "Yoga Analysis",           access: "insight"                     },
  { icon: "⏰", title: "Vimshottari Dasha",       access: "insight"                     },
];

/* ─── Plan definitions ───────────────────────────────────────── */
const PLANS: {
  name: string;
  price: string;
  strike?: string;
  current: boolean;
  popular: boolean;
  items: string[];
}[] = [
  {
    name: "STARTER",
    price: "Free",
    current: true,
    popular: false,
    items: [
      "Birth chart snapshot",
      "Karmic Overview — all four domains (full)",
      "Personality highlights — Lagna + Moon",
    ],
  },
  {
    name: "GROWTH",
    price: "₹299",
    strike: "₹599",
    current: false,
    popular: false,
    items: [
      "13-chapter PDF — incl. Report Snapshot & AI Summary",
      "Full remedies, awareness signals & planet effects",
      "Dasha timing (current period) + PDF export",
    ],
  },
  {
    name: "INSIGHT",
    price: "₹499",
    strike: "₹999",
    current: false,
    popular: true,
    items: [
      "Everything in GROWTH",
      "Full Yoga analysis, conjunctions & planet scoring",
      "Lifetime Dasha map — ~100-year timeline table",
    ],
  },
];

/* ─── Shared card shell ──────────────────────────────────────── */
const CARD: React.CSSProperties = {
  background: "rgba(2,6,23,0.78)",
  border: "1px solid rgba(148,163,184,0.15)",
  borderRadius: 14,
  padding: 14,
  lineHeight: 1.2,
};

/* ═══════════════════════════════════════════════════════════════
   Main panel — mobile-first, desktop reorders sections
═══════════════════════════════════════════════════════════════ */
export default function ReportOrientationPanel() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return (
    <div style={{ display: "grid", gap: 20 }}>
      {isMobile ? (
        <>
          <PrivacyNext />
          <ReportSections />
          <PlanAccess />
        </>
      ) : (
        <>
          <ReportSections />
          <PlanAccess />
          <PrivacyNext />
        </>
      )}
    </div>
  );
}

/* ─── Section 1: What You'll Get ────────────────────────────── */
function ReportSections() {
  return (
    <div style={CARD}>
      <div
        style={{
          fontSize: 14,
          fontWeight: 650,
          color: "#fbbf24",
          marginBottom: 12,
          letterSpacing: 0.3,
        }}
      >
        What You'll Get — 13 Chapters
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "4px 8px",
        }}
      >
        {SECTIONS.map((sec, i) => {
          const acc = ACCESS[sec.access];
          return (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "5px 0",
                borderBottom: "1px solid rgba(148,163,184,0.07)",
              }}
            >
              <span style={{ fontSize: 13, flexShrink: 0, lineHeight: 1 }}>
                {sec.icon}
              </span>
              <span
                style={{
                  fontSize: 11.5,
                  color: sec.highlight
                    ? "rgba(245,158,11,0.88)"
                    : "rgba(229,231,235,0.75)",
                  fontWeight: sec.highlight ? 650 : 400,
                  flex: 1,
                  lineHeight: 1.3,
                }}
              >
                {sec.title}
                {sec.pdfOnly && (
                  <span style={{
                    fontSize: 8,
                    color: "rgba(148,163,184,0.5)",
                    marginLeft: 3,
                    fontWeight: 400,
                  }}>
                    PDF
                  </span>
                )}
              </span>
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  padding: "2px 5px",
                  borderRadius: 999,
                  background: acc.bg,
                  border: `1px solid ${acc.border}`,
                  color: acc.color,
                  letterSpacing: "0.04em",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
              >
                {acc.label}
              </span>
            </div>
          );
        })}
      </div>

      <div
        style={{
          marginTop: 11,
          fontSize: 11.5,
          color: "rgba(148,163,184,0.55)",
          lineHeight: 1.5,
        }}
      >
        Chart structure fixed at birth — timing and guidance evolve with your active Dasha.{" "}
        <a
          href="/sample-report.pdf"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "rgba(245,158,11,0.65)", textDecoration: "none" }}
        >
          View sample →
        </a>
      </div>
    </div>
  );
}

/* ─── Section 2: Plan Access & Depth ────────────────────────── */
function PlanAccess() {
  return (
    <div style={CARD}>
      <div
        style={{
          fontSize: 14,
          fontWeight: 650,
          color: "#f59e0b",
          marginBottom: 12,
          letterSpacing: 0.3,
        }}
      >
        Report Access & Depth
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        {PLANS.map((plan, i) => (
          <div
            key={i}
            style={{
              padding: "10px 12px",
              borderRadius: 10,
              background: plan.current
                ? "rgba(245,158,11,0.07)"
                : plan.popular
                ? "rgba(251,191,36,0.03)"
                : "rgba(255,255,255,0.02)",
              border: `1px solid ${
                plan.current
                  ? "rgba(245,158,11,0.28)"
                  : plan.popular
                  ? "rgba(251,191,36,0.18)"
                  : "rgba(148,163,184,0.09)"
              }`,
            }}
          >
            {/* Header row: name + badges | price */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 7,
                gap: 6,
              }}
            >
              {/* Left: name + status badges */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flexWrap: "wrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    fontSize: 11.5,
                    fontWeight: 800,
                    letterSpacing: "0.06em",
                    color:
                      plan.current || plan.popular ? "#fbbf24" : "#94a3b8",
                  }}
                >
                  {plan.name}
                </span>

                {plan.popular && (
                  <span
                    style={{
                      fontSize: 9,
                      background: "rgba(251,191,36,0.12)",
                      border: "1px solid rgba(251,191,36,0.28)",
                      color: "#fbbf24",
                      padding: "1px 6px",
                      borderRadius: 999,
                      fontWeight: 700,
                      letterSpacing: "0.03em",
                      whiteSpace: "nowrap",
                    }}
                  >
                    ⭐ Popular
                  </span>
                )}

                {plan.current && (
                  <span
                    style={{
                      fontSize: 9.5,
                      color: "rgba(245,158,11,0.75)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    ← You are here
                  </span>
                )}
              </div>

              {/* Right: price */}
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 4,
                  flexShrink: 0,
                }}
              >
                {plan.strike && (
                  <span
                    style={{
                      fontSize: 10.5,
                      textDecoration: "line-through",
                      color: "#4b5563",
                    }}
                  >
                    {plan.strike}
                  </span>
                )}
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: plan.price === "Free" ? "#34d399" : "#fbbf24",
                  }}
                >
                  {plan.price}
                </span>
              </div>
            </div>

            {/* Feature bullets */}
            <ul style={{ margin: 0, paddingLeft: 14, display: "grid", gap: 3 }}>
              {plan.items.map((item, j) => (
                <li
                  key={j}
                  style={{ fontSize: 11.5, color: "#94a3b8", lineHeight: 1.5 }}
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 10,
          gap: 8,
        }}
      >
        <a
          href="/plan-features"
          style={{
            color: "#fbbf24",
            textDecoration: "none",
            fontSize: 12,
            opacity: 0.85,
          }}
        >
          Full plan comparison →
        </a>
        <span style={{ fontSize: 11, color: "#4b5563", textAlign: "right" }}>
          Upgrade anytime — no recalculation
        </span>
      </div>
    </div>
  );
}

/* ─── Section 3: Private & Instant ──────────────────────────── */
function PrivacyNext() {
  return (
    <div style={CARD}>
      {/* Header with policy links */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 650,
            color: "#fbbf24",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          🔒 Private & Instant
        </div>
        <div style={{ display: "flex", gap: 12, fontSize: 11.5 }}>
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#6b7280", textDecoration: "none" }}
          >
            Privacy Policy
          </a>
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#6b7280", textDecoration: "none" }}
          >
            Terms
          </a>
        </div>
      </div>

      {/* Privacy note */}
      <div
        style={{
          fontSize: 12.5,
          color: "#94a3b8",
          lineHeight: 1.65,
          marginBottom: 14,
        }}
      >
        Your birth data is used only to calculate this chart. Nothing is stored
        permanently — no accounts, no profiles, no tracking.
      </div>

      {/* What happens next */}
      <div
        style={{
          fontSize: 13,
          fontWeight: 650,
          color: "#fbbf24",
          marginBottom: 9,
        }}
      >
        After You Click Generate
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        {[
          {
            icon: "⚡",
            text: "Your chart is calculated instantly from your birth data.",
          },
          {
            icon: "🔐",
            text: "Locked sections are clearly labelled — no surprise charges.",
          },
          {
            icon: "⬆️",
            text: "Upgrade unlocks depth immediately — no recalculation needed.",
          },
        ].map((item, i) => (
          <div
            key={i}
            style={{ display: "flex", alignItems: "flex-start", gap: 8 }}
          >
            <span
              style={{ fontSize: 13, lineHeight: 1.4, flexShrink: 0 }}
            >
              {item.icon}
            </span>
            <span style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.55 }}>
              {item.text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
